import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import {
  Paper,
  Group,
  Button,
  Title,
  Box,
  Alert,
  LoadingOverlay,
} from '@mantine/core';
import { IconPlus, IconInfoCircle } from '@tabler/icons-react';

import usePartner from '../hooks/partners/usePartner.ts';
import useChainStoreContract from '../hooks/chain-store-contracts/useChainStoreContract.ts';
import useCreateChainStoreContract from '../hooks/chain-store-contracts/useCreateChainStoreContract.ts';

import type {
  MutateChainStoreContractFormValues,
  MutateChainStoreContractFormInitialValues,
} from '../components/ui/MutateChainStoreContractForm.tsx';

import MutateChainStoreContractForm from '../components/ui/MutateChainStoreContractForm.tsx';
import PreviousPageButton from '../components/ui/PreviousPageButton.tsx';

export default function ChainStoreContractNewPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const cloneId = state?.cloneId;

  const { partnerId: partnerId } = useParams();
  const { partner, isLoading: isPartnerLoading } = usePartner(partnerId);
  const { data: cloneContract, isLoading: isCloneContractLoading } =
    useChainStoreContract(partnerId, cloneId, !!cloneId);

  const {
    mutate: createChainStoreContract,
    isPending: isCreateChainStoreContractPending,
  } = useCreateChainStoreContract();

  const [duplicateNotice, setDuplicateNotice] = useState(!!cloneId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isPartnerLoading || isCloneContractLoading) {
    return (
      <LoadingOverlay visible loaderProps={{ color: 'cyan', type: 'bars' }} />
    );
  }

  // const
  const initialFormValues: MutateChainStoreContractFormInitialValues =
    cloneId && cloneContract
      ? {
          name: '',
          profileType: cloneContract.profileType,
          profitSharingMethod: cloneContract.profitSharingMethod,
          profitSharingValue:
            cloneContract.profitSharingMethod === 'FIXED'
              ? cloneContract.basicFixedFee
              : cloneContract.basicPercentage,
          startAt: null,
          endAt: null,
          chainStoreNotificationEndpoint: '',
          requestAmountLimit: 50000,
          receiptMode: cloneContract.receiptMode,
          existingAttachments: [],
        }
      : {
          name: '',
          profileType: 'DEFAULT',
          profitSharingMethod: 'FIXED',
          profitSharingValue: '0.0',
          startAt: null,
          endAt: null,
          chainStoreNotificationEndpoint: '',
          requestAmountLimit: 50000,
          receiptMode: 'ONSITE_PARTNER',
          existingAttachments: [],
        };

  // callback functions
  function onSubmit(values: MutateChainStoreContractFormValues) {
    if (isCreateChainStoreContractPending) return;

    createChainStoreContract(
      {
        partnerId: partnerId,
        name: values.name,
        profileType: values.profileType,
        profitSharingMethod: values.profitSharingMethod,
        basicFixedFee:
          values.profitSharingMethod === 'FIXED'
            ? values.profitSharingValue
            : '0.0',
        basicPercentage:
          values.profitSharingMethod === 'PERCENTAGE'
            ? values.profitSharingValue
            : '0.0',
        startAt: values.startAt,
        endAt: values.endAt,
        chainStoreNotificationEndpoint: values.chainStoreNotificationEndpoint,
        receiptMode: values.receiptMode,
        file: values.newAttachments[0].file,
      },
      {
        onSuccess: (id) => {
          // 轉導至 editPage
          navigate(`/partners/${partnerId}/chain-store-contracts/${id}/edit`, {
            state: { created: true },
          });
        },
      },
    );
  }

  return (
    <>
      <PreviousPageButton
        href={`/partners/${partnerId}`}
        content={'返回業者詳情頁'}
      />
      {duplicateNotice && (
        <Alert
          variant="light"
          color="green"
          mb={20}
          icon={<IconInfoCircle />}
          withCloseButton
          onClose={() => setDuplicateNotice(false)}
          title={`成功複製「${cloneContract.name}」合約`}
        >
          僅複製「合約套用方式」、「分潤條件」、「發票設定」等欄位，其餘欄位仍需手動填寫
        </Alert>
      )}

      <Paper radius="lg" p="xl" shadow="xs" mb={70}>
        <Title order={3}>{`${partner.name} | 連鎖店合約`}</Title>
        <MutateChainStoreContractForm
          initialValues={initialFormValues}
          onSubmit={onSubmit}
        />
      </Paper>

      <Box
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        h={70}
        p="md"
        bg="var(--mantine-color-white)"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          zIndex: 100,
        }}
      >
        <Group justify="flex-end">
          <Button
            type="submit"
            form="mutateChainStoreContractForm"
            loading={isCreateChainStoreContractPending}
            fw={400}
            leftSection={<IconPlus />}
          >
            新增
          </Button>
        </Group>
      </Box>
    </>
  );
}
